import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const milestones = [
  { year: '2000', title: 'Fundación', desc: 'IngemetalSur nace en Temuco, Región de La Araucanía.', highlight: false },
  { year: '2005', title: 'Primera minería', desc: 'Contrato con Minera El Toqui en Coyhaique, Aysén.', highlight: false },
  { year: '2007', title: 'Premio SERNAGEOMIN', desc: 'Reconocimiento por cero accidentes en proyectos mineros.', highlight: true },
  { year: '2010', title: 'Consolidación minera', desc: 'Segundo contrato con Minera El Toqui. Experiencia validada.', highlight: false },
  { year: '2014', title: 'Expansión sectorial', desc: 'Piscicultura Melipeuco y proyectos agrícolas.', highlight: false },
  { year: '2018', title: 'Retail industrial', desc: 'Concesionaria Volvo y Mall Chino Torremolino en Temuco.', highlight: false },
  { year: '2022', title: 'Planta industrial', desc: 'Planta de semillas Chillán y proyectos de gran escala.', highlight: false },
  { year: '2024', title: '+25 años', desc: 'Trayectoria consolidada con cobertura nacional.', highlight: false },
];

const coverage = [
  'Temuco', 'Coyhaique', 'Concepción', 'Chillán', 'Puerto Montt',
  'Los Ángeles', 'Villarrica', 'Pucón', 'Santiago', 'Valparaíso',
  'La Serena', 'Antofagasta', 'Calama', 'Iquique', 'Talca',
  'Copiapó', 'Osorno', 'Valdivia', 'Lautaro', 'Coronel',
];

const capabilities = [
  { icon: 'ri-building-2-line', title: '2.500 m²', desc: 'Capacidad de fabricación en taller' },
  { icon: 'ri-team-line', title: '+40', desc: 'Técnicos y operarios calificados' },
  { icon: 'ri-truck-line', title: 'Nacional', desc: 'Logística propia a todo Chile' },
  { icon: 'ri-shield-check-line', title: 'Certificado', desc: 'Estándares SERNAGEOMIN' },
];

const values = [
  {
    icon: 'ri-shield-check-line',
    title: 'Seguridad',
    desc: 'Cero accidentes. Premio SERNAGEOMIN 2007.',
  },
  {
    icon: 'ri-medal-2-line',
    title: 'Calidad',
    desc: 'Materiales de primera y controles estrictos.',
  },
  {
    icon: 'ri-time-line',
    title: 'Cumplimiento',
    desc: 'Plazos acordados. Puntualidad garantizada.',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Confianza',
    desc: 'Relaciones de largo plazo con resultados.',
  },
];

const reveal = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal ${r.isVisible ? 'reveal-visible' : ''}`;
const revealLeft = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-left ${r.isVisible ? 'reveal-visible' : ''}`;
const revealScale = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-scale ${r.isVisible ? 'reveal-visible' : ''}`;

const NosotrosPage = () => {
  const heroR = useScrollAnimation();
  const statsR = useScrollAnimation();
  const premioR = useScrollAnimation();
  const premioImgR = useScrollAnimation();
  const aboutR = useScrollAnimation();
  const aboutImgR = useScrollAnimation();
  const timelineR = useScrollAnimation();
  const valuesR = useScrollAnimation();
  const coverageR = useScrollAnimation();
  const ctaR = useScrollAnimation();
  const socialR = useScrollAnimation();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 overflow-hidden bg-[#0C1D2E]">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20steel%20construction%20company%20headquarters%20in%20Chile%2C%20professional%20corporate%20building%20with%20metal%20framework%20and%20glass%20facade%2C%20dramatic%20sunset%20sky%20with%20warm%20amber%20and%20deep%20blue%20tones%2C%20wide%20angle%20architectural%20photography%2C%20clean%20professional%20industrial%20aesthetic%2C%20no%20people%2C%20building%20illuminated%20by%20golden%20hour%20light%20against%20deep%20blue%20sky%2C%20premium%20corporate%20photography%20style&width=1920&height=700&seq=about004&orientation=landscape"
              alt="IngemetalSur - Empresa constructora industrial profesional en Chile"
              className="w-full h-full object-cover object-center opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1D2E]/80 via-[#0C1D2E]/50 to-[#0C1D2E]/30"></div>
          </div>
          <div ref={heroR.ref} className={`relative z-10 max-w-7xl mx-auto px-4 md:px-8 ${reveal(heroR)}`}>
            <div className="max-w-2xl">
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">QUIÉNES SOMOS</span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mt-3 mb-5 leading-[1.1]">
                Más de 25 años<br />
                <span className="text-[#C88A2A]">construyendo Chile</span>
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed mb-8 max-w-lg">
                Desde el año 2000 construimos infraestructura industrial con enfoque técnico, seguridad certificada y cobertura nacional.
              </p>
              <Link
                to="/proyectos"
                className="inline-flex items-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                Ver proyectos <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-[#0C1D2E] border-b border-white/10">
          <div ref={statsR.ref} className={`max-w-7xl mx-auto px-4 md:px-8 ${reveal(statsR)}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
              {[
                { value: '+25', label: 'Años de experiencia' },
                { value: '+300', label: 'Proyectos ejecutados' },
                { value: '20+', label: 'Ciudades de cobertura' },
                { value: '1', label: 'Premio SERNAGEOMIN' },
              ].map((stat, i) => (
                <div key={stat.label} className={`py-10 md:py-12 px-4 md:px-6 text-center reveal reveal-delay-${(i + 1) * 100} ${statsR.isVisible ? 'reveal-visible' : ''}`}>
                  <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">{stat.value}</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Premio SERNAGEOMIN — Premium Section */}
        <section id="premio-sernageomin" className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
              {/* Image */}
              <div ref={premioImgR.ref} className={`relative ${revealLeft(premioImgR)}`}>
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#C88A2A]/10 rounded-full"></div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#C88A2A]/10 rounded-full"></div>
                <img
                  src="https://i.imgur.com/Z0EvHT4.jpg"
                  alt="Premio SERNAGEOMIN por seguridad minera - Trofeo reconocimiento IngemetalSur"
                  className="relative z-10 w-full h-auto rounded-lg object-cover shadow-xl hover:shadow-2xl transition-shadow duration-500"
                />
                <div className="absolute bottom-6 left-6 z-20 bg-[#0C1D2E] text-white px-5 py-3 rounded-md">
                  <div className="text-xs text-white/50 uppercase tracking-wider">Año</div>
                  <div className="text-2xl font-heading font-bold">2007</div>
                </div>
              </div>

              {/* Content */}
              <div ref={premioR.ref} className={`${reveal(premioR)}`}>
                <div className="inline-flex items-center gap-2 bg-[#C88A2A]/10 border border-[#C88A2A]/20 rounded-full px-4 py-2 mb-6">
                  <i className="ri-award-fill text-[#C88A2A] text-sm"></i>
                  <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-wider">Reconocimiento Nacional</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mb-5 leading-tight">
                  Premio SERNAGEOMIN<br />
                  <span className="text-[#475569]">Seguridad Minera</span>
                </h2>
                <p className="text-[#475569] text-base leading-relaxed mb-6">
                  En 2007, IngemetalSur recibió el reconocimiento del Servicio Nacional de Geología y Minería por mantener <strong className="text-[#0C1D2E]">cero accidentes laborales</strong> en proyectos mineros, incluyendo trabajos para Minera El Toqui en Coyhaique, Región de Aysén.
                </p>
                <p className="text-[#475569] text-base leading-relaxed mb-8">
                  Este galardón refleja nuestra cultura de seguridad inquebrantable y el cumplimiento de los estándares más exigentes de la industria minera chilena.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: 'ri-shield-check-line', label: 'Cero accidentes' },
                    { icon: 'ri-map-pin-line', label: 'Minera El Toqui' },
                    { icon: 'ri-calendar-line', label: '2007' },
                    { icon: 'ri-hammer-line', label: 'Minería certificada' },
                  ].map((badge, i) => (
                    <div key={badge.label} className={`flex items-center gap-2 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 hover:border-[#C88A2A]/30 transition-colors duration-200 reveal-delay-${(i + 1) * 100} ${premioR.isVisible ? 'reveal reveal-visible' : 'reveal'}`}>
                      <i className={`${badge.icon} text-[#C88A2A] text-sm`}></i>
                      <span className="text-[#0C1D2E] text-xs font-semibold">{badge.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUIÉNES SOMOS */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left: QUIÉNES SOMOS */}
              <div ref={aboutR.ref} className={`${revealLeft(aboutR)}`}>
                <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">QUIÉNES SOMOS</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3 mb-6 leading-tight">
                  Ingeniería, construcción y experiencia al servicio de la industria.
                </h2>
                <p className="text-[#475569] text-base leading-relaxed mb-4">
                  Con más de 25 años de trayectoria, IngemetalSur se ha consolidado como una empresa referente en construcción industrial y obras civiles a nivel nacional. Nuestra experiencia abarca el desarrollo de galpones industriales, infraestructura comercial, estructuras metálicas y proyectos especializados para sectores productivos de alta exigencia.
                </p>
                <p className="text-[#475569] text-base leading-relaxed mb-4">
                  Operamos bajo una cultura enfocada en la seguridad, la eficiencia operacional y el cumplimiento técnico, ejecutando cada proyecto con estándares que garantizan calidad, continuidad y confianza.
                </p>
                <p className="text-[#475569] text-base leading-relaxed mb-8">
                  En IngemetalSur transformamos experiencia en infraestructura sólida, desarrollando soluciones constructivas que aportan valor, durabilidad y respaldo a largo plazo.
                </p>

                {/* Capabilities mini */}
                <div className="grid grid-cols-2 gap-4">
                  {capabilities.map((cap, i) => (
                    <div key={cap.title} className={`bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-4 hover:border-[#C88A2A]/20 transition-all duration-300 hover:shadow-md reveal-delay-${(i + 1) * 100} ${aboutR.isVisible ? 'reveal reveal-visible' : 'reveal'}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-[#0C1D2E] rounded-md shrink-0 hover:scale-110 transition-transform duration-200">
                          <i className={`${cap.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <div className="font-heading font-bold text-[#0C1D2E] text-base">{cap.title}</div>
                          <div className="text-[#64748B] text-xs">{cap.desc}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Image */}
              <div ref={aboutImgR.ref} className={`relative ${reveal(aboutImgR)}`}>
              <img
                  src="https://public.readdy.ai/ai/img_res/edited_68555912a2c75a92642b4f844bbc0d74_714ba0c3.jpg"
                  alt="Galpón industrial Fundo Aguasbuenas - Proyecto IngemetalSur en Chile"
                  className="w-full h-[520px] md:h-[640px] object-cover object-center rounded-lg hover:shadow-2xl transition-shadow duration-500"
                />
                <div className="absolute -bottom-5 -left-5 bg-[#0C1D2E] text-white p-5 rounded-lg hover:scale-105 transition-transform duration-200">
                  <div className="text-3xl font-heading font-bold">+25</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">Años de experiencia</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-24 bg-[#0C1D2E]">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div ref={timelineR.ref} className={`text-center mb-16 ${reveal(timelineR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Trayectoria</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-3">
                Hitos que nos definen
              </h2>
            </div>

            <div className="relative">
              {/* Center line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2"></div>

              <div className="space-y-12">
                {milestones.map((m, i) => (
                  <div key={m.year} className={`relative flex items-start gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} reveal ${timelineR.isVisible ? 'reveal-visible' : ''}`} style={{ transitionDelay: `${i * 100}ms` }}>
                    {/* Spacer for desktop */}
                    <div className="flex-1 hidden md:block"></div>

                    {/* Dot */}
                    <div className={`absolute left-4 md:left-1/2 w-3 h-3 rounded-full -translate-x-1/2 mt-2 z-10 ${m.highlight ? 'bg-[#D4A03D] ring-6 ring-[#D4A03D]/30 shadow-[0_0_12px_rgba(212,160,61,0.5)]' : 'bg-white/20'}`}></div>

                    {/* Card */}
                    <div className="flex-1 ml-10 md:ml-0">
                      <div className={`rounded-lg p-6 hover:shadow-lg transition-all duration-300 ${m.highlight ? 'bg-[#D4A03D]/10 border border-[#D4A03D]/30 hover:border-[#D4A03D]/50' : 'bg-white/5 border border-white/10 hover:border-white/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`font-heading font-bold text-xl ${m.highlight ? 'text-[#D4A03D]' : 'text-white'}`}>{m.year}</span>
                          {m.highlight && (
                            <span className="inline-flex items-center gap-1 bg-[#D4A03D] text-[#0A0A0A] text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase">
                              <i className="ri-award-fill text-xs"></i>
                              Premio
                            </span>
                          )}
                        </div>
                        <h3 className="text-white font-semibold text-base mb-1">{m.title}</h3>
                        <p className="text-white/40 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={valuesR.ref} className={`text-center mb-14 ${reveal(valuesR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Nuestros valores</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                Lo que nos define
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v, i) => (
                <div key={v.title} className={`bg-white border border-[#E2E8F0] rounded-lg p-7 hover:border-[#C88A2A]/20 hover:shadow-lg transition-all duration-300 reveal ${valuesR.isVisible ? 'reveal-visible' : ''}`} style={{ transitionDelay: `${i * 120}ms` }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0C1D2E] rounded-md mb-5 hover:scale-110 hover:bg-[#C88A2A] transition-all duration-200">
                    <i className={`${v.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-[#0C1D2E] text-base mb-2 uppercase tracking-wide">{v.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coverage */}
        <section className="py-24 bg-[#0C1D2E]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={coverageR.ref} className={`text-center mb-12 ${reveal(coverageR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Cobertura</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-3 mb-4">
                Operamos en todo Chile
              </h2>
              <p className="text-white/40 text-base max-w-lg mx-auto">
                Desde Aysén hasta el Biobío. Llevamos infraestructura industrial a cada región.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {coverage.map((city, i) => (
                <div
                  key={city}
                  className={`flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-5 py-2.5 hover:bg-white/15 hover:border-white/30 hover:scale-105 transition-all duration-200 cursor-pointer reveal ${coverageR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <i className="ri-map-pin-line text-[#C88A2A] text-sm"></i>
                  <span className="text-white/70 font-medium text-sm">{city}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-white">
          <div ref={ctaR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${revealScale(ctaR)}`}>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#0C1D2E] mb-4">
              ¿Tienes un proyecto en mente?
            </h2>
            <p className="text-[#475569] text-base mb-8">
              Contáctanos hoy y recibe una cotización sin compromiso. Trabajamos en todo Chile.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp
              </a>
              <a
                href="mailto:contacto@ingemetalsur.cl"
                className="inline-flex items-center justify-center gap-2 bg-[#0C1D2E] hover:bg-[#1A3A5C] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-mail-line text-lg"></i>
                contacto@ingemetalsur.cl
              </a>
            </div>
          </div>
        </section>

        {/* Social Media */}
        <section className="py-16 bg-white">
          <div ref={socialR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${reveal(socialR)}`}>
            <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Síguenos</span>
            <h2 className="text-2xl md:text-3xl font-black text-[#0C1D2E] mt-2 mb-3">
              Conecta con nosotros
            </h2>
            <p className="text-[#475569] text-base mb-8">
              Sigue nuestras obras y proyectos en redes sociales. Compartimos fotos, videos y novedades de IngemetalSur.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.instagram.com/ingemetalsurtco"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#0C1D2E] hover:bg-[#1A3A5C] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap text-base border border-[#C88A2A]/30"
              >
                <i className="ri-instagram-line text-xl"></i>
                Instagram
              </a>
              <a
                href="https://www.facebook.com/ingemetalsur"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166fe5] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap text-base"
              >
                <i className="ri-facebook-circle-line text-xl"></i>
                Facebook
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default NosotrosPage;